import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/common/Modal';
import { usePosStore } from '../../stores/pos.store';
import { useAuthStore } from '../../stores/auth.store';
import { useGenerateInvoice } from '../../hooks/useInvoices';
import { formatCurrency } from '../../lib/currency';
import type { InvoiceLineItem } from '@motra/shared';

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (invoiceId: string) => void;
}

type PaymentTab = 'cash' | 'card' | 'qr' | 'credit';

export default function PaymentModal({ open, onClose, onSuccess }: PaymentModalProps) {
  const { t } = useTranslation();
  const shop = useAuthStore((s) => s.shop);
  const user = useAuthStore((s) => s.user);
  const currSym = shop?.curr_sym ?? '$';

  const { basket, customer, discount, taxes, getTotal, getSubtotal, clearBasket } = usePosStore();
  const generateInvoice = useGenerateInvoice();

  const [activeTab, setActiveTab] = useState<PaymentTab>('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [creditPin, setCreditPin] = useState('');
  const [error, setError] = useState('');

  const total = getTotal();
  const subtotal = getSubtotal();
  const received = parseFloat(cashReceived) || 0;
  const change = Math.max(0, received - total);

  async function handleConfirm() {
    if (!shop?.id) return;
    setError('');

    if (activeTab === 'cash' && received < total) {
      setError('Amount received is less than total.');
      return;
    }

    const items: InvoiceLineItem[] = basket.map((b, i) => ({
      id: `${b.item.id}_${i}`,
      name: b.item.name ?? 'Item',
      qty: b.qty,
      price: b.item.pprice ?? b.item.price ?? 0,
      cost: b.item.cost,
      discount: b.discount?.val,
      discount_type: b.discount?.type,
      category: b.item.category,
      barcode: b.item.barcode,
    }));

    const taxTotal = taxes.reduce((sum, t) => {
      const discountedSubtotal = discount
        ? discount.type === 'percentage'
          ? subtotal * (1 - discount.val / 100)
          : Math.max(0, subtotal - discount.val)
        : subtotal;
      return sum + discountedSubtotal * (t.value / 100);
    }, 0);

    try {
      const res = await generateInvoice.mutateAsync({
        shopid: shop.id,
        customer_id: customer?.id,
        user_id: user?.id,
        status: 'completed',
        items: JSON.stringify(items),
        subtotal,
        discount: discount ? JSON.stringify(discount) : undefined,
        tax: taxes.length > 0 ? taxes[0].value : undefined,
        tax_val: taxTotal,
        total,
        payment_type: activeTab,
        tendered: activeTab === 'cash' ? received : total,
        change: activeTab === 'cash' ? change : 0,
      });

      if (res.success && res.data) {
        clearBasket();
        onSuccess(res.data.id);
      } else {
        setError(res.error ?? 'Failed to generate invoice');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error processing payment');
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Payment" size="md">
      <div className="payment-modal">
        {/* Tab selector */}
        <div className="payment-tabs">
          {(['cash', 'card', 'qr', 'credit'] as PaymentTab[]).map((tab) => (
            <button
              key={tab}
              className={`payment-tab${activeTab === tab ? ' payment-tab--active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'cash' && `💵 ${t('cash', 'Cash')}`}
              {tab === 'card' && `💳 ${t('card', 'Card')}`}
              {tab === 'qr' && '📱 QR'}
              {tab === 'credit' && `💰 ${t('credits', 'Credits')}`}
            </button>
          ))}
        </div>

        {/* Total summary */}
        <div className="payment-total">
          <span>{t('total', 'Total')}</span>
          <span className="payment-total__amount">{formatCurrency(total, currSym)}</span>
        </div>

        {/* Tab content */}
        <div className="payment-content">
          {activeTab === 'cash' && (
            <div className="payment-cash">
              <div className="form-group">
                <label className="form-label">{t('tendered', 'Amount Tendered')}</label>
                <input
                  type="number"
                  className="form-input form-input--lg"
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min={0}
                  autoFocus
                />
              </div>
              <div className="payment-change">
                <span>{t('change', 'Change')}</span>
                <span className="payment-change__amount">{formatCurrency(change, currSym)}</span>
              </div>
              {/* Quick amount buttons */}
              <div className="payment-quick-amounts">
                {[total, Math.ceil(total / 10) * 10, Math.ceil(total / 50) * 50, Math.ceil(total / 100) * 100].filter((v, i, arr) => arr.indexOf(v) === i).map((amt) => (
                  <button
                    key={amt}
                    className="btn btn--outline btn--sm"
                    onClick={() => setCashReceived(String(amt))}
                  >
                    {formatCurrency(amt, currSym)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'card' && (
            <div className="payment-card">
              <p className="payment-card__note">
                Card payment via Stripe. Enter card details below.
              </p>
              {/* Stripe Elements placeholder */}
              <div className="stripe-elements-placeholder">
                <p>Stripe Elements will be mounted here</p>
                <p className="text-muted">Configure STRIPE_PUB_KEY in account settings.</p>
              </div>
            </div>
          )}

          {activeTab === 'qr' && (
            <div className="payment-qr">
              <p className="payment-qr__note">
                Generate QR code for e-wallet or bank transfer payment.
              </p>
              <div className="payment-qr__placeholder">
                <div className="qr-code-placeholder">QR</div>
                <p>QR code will appear here after generation.</p>
              </div>
              <button className="btn btn--primary">Generate QR Code</button>
            </div>
          )}

          {activeTab === 'credit' && (
            <div className="payment-credit">
              <p>
                {customer
                  ? `Credits available: ${formatCurrency(customer.credits ?? 0, currSym)}`
                  : 'No customer linked. Please link a customer first.'}
              </p>
              {customer && (
                <div className="form-group">
                  <label className="form-label">{t('credit_pin', 'Credit PIN')}</label>
                  <input
                    type="password"
                    className="form-input"
                    value={creditPin}
                    onChange={(e) => setCreditPin(e.target.value)}
                    placeholder="Enter PIN"
                    maxLength={6}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {error && <div className="alert alert--error">{error}</div>}

        <div className="payment-modal__actions">
          <button className="btn btn--outline" onClick={onClose}>
            {t('cancel', 'Cancel')}
          </button>
          <button
            className="btn btn--primary btn--lg"
            onClick={handleConfirm}
            disabled={generateInvoice.isPending}
          >
            {generateInvoice.isPending ? 'Processing...' : `Confirm ${formatCurrency(total, currSym)}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
